import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Loader, Stack, Tooltip } from "@mantine/core";
import { modals } from "@mantine/modals";
import dayjs from "dayjs";
import { useEffect } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import z from "zod";

import { Input } from "@/components/_Input";
import { useSession } from "@/hooks";
import { EditSchedulingArgs, useLazyGetOrderHasDriverQuery, useLazyGetTruckQuery } from "@/store";
import { editSchedulingArgsSchema } from "@/store/apis/nextcargo/scheduling";
import { cn } from "@/utils";

import { useSearchDrivers } from "../_hooks/useSearchDrivers";
import { useSearchTrucks } from "../_hooks/useSearchTrucks";

type ExtendedEditSchedulingArgsSchema = z.infer<typeof extendedEditSchedulingArgsSchema>;
export const extendedEditSchedulingArgsSchema = editSchedulingArgsSchema
  .extend({
    isFreelanceDriver: z.boolean(),
    capacity: z.number({ error: "Capacidade é obrigatória" })
  })
  .superRefine((data, ctx) => {
    if (data.qtyOrdered > data.capacity) {
      ctx.addIssue({
        code: "custom",
        message: `Capacidade não pode ser maior que a capacidade bruta (${data.qtyOrdered}t) do caminhão`,
        path: ["capacity"]
      });
    }
  });

export type EditSchedulingArgsAsProps = Pick<EditSchedulingArgs, "company" | "driver" | "order" | "truck" | "id">;

type SchedulingFormImplProps = {
  schedule?: EditSchedulingArgsAsProps;
  defaultValues?: {
    expirationDate?: string;
    qtyOrdered?: number;
  };
  onSubmit: (data: ExtendedEditSchedulingArgsSchema) => void;
  onDeny?: () => void;
  onConfirm?: () => void;
};

function SchedulingFormImpl({ schedule, defaultValues, onSubmit: onSubmitCallback, onDeny }: SchedulingFormImplProps) {
  const [orderHasDriverTrigger] = useLazyGetOrderHasDriverQuery();

  const { company, shippingCompanyId } = useSession();
  const companyId = company?.id || shippingCompanyId;

  const {
    data: searchTrucksResult,
    actions: { setSearchTruck },
    state: { searchTruck, isFetching }
  } = useSearchTrucks({ search: schedule?.truck?.licensePlate || "" });
  const {
    data: searchDriverResults,
    actions: { setSearchDriver },
    state: { searchDriver, isFetching: isFetchingDrivers }
  } = useSearchDrivers({ search: schedule?.driver?.name || "" });

  const [getTruckTrigger, { data: truckData, isFetching: isFetchingTruck }] = useLazyGetTruckQuery();

  const {
    formState: { isSubmitting, isDirty },
    handleSubmit,
    setValue,
    control,
    reset,
    watch
  } = useForm({
    mode: "all",
    resolver: zodResolver(extendedEditSchedulingArgsSchema),
    defaultValues: {
      id: schedule?.id,
      order: {
        id: schedule?.order.id
      },
      truck: {
        id: schedule?.truck?.id || "",
        licensePlate: schedule?.truck?.licensePlate || ""
      },
      company: {
        id: schedule?.company?.id || companyId
      },
      driver: {
        id: schedule?.driver?.id || "",
        name: schedule?.driver?.name || ""
      },
      isFreelanceDriver: false,
      capacity: 0,
      qtyOrdered: defaultValues?.qtyOrdered || 0,
      expirationDate: defaultValues?.expirationDate || dayjs().format("YYYY-MM-DD"),
      qtyTransported: 0
    }
  });
  const onSubmit: SubmitHandler<ExtendedEditSchedulingArgsSchema> = async (payload) => {
    const { driver } = payload;
    const { isSuccess } = await orderHasDriverTrigger({ orderId: String(schedule?.order.id), driverId: String(driver.id) });

    if (!isSuccess) return;

    onSubmitCallback(payload);
    modals.closeAll();
  };

  const [isFreelanceDriver, truckId] = watch(["isFreelanceDriver", "truck.id"]);

  const today = dayjs().format("YYYY-MM-DD");
  const disabledStyles = cn({ "pointer-events-none cursor-not-allowed opacity-50": !isFreelanceDriver });

  const handleDeny = () => {
    onDeny?.();
    modals.closeAll();
    reset();
  };

  type HandlerType = "truck" | "driver";
  const handleSearch = (type: HandlerType) => (value: string) => {
    if (type !== "truck") return setSearchDriver(value);
    setSearchTruck(value);
  };
  const handleClear = (type: HandlerType) => () => {
    const matcher: Record<typeof type, () => void> = Object.freeze({
      truck: () => {},
      driver: () => {}
    });

    if (!(type in matcher)) return console.error(`Invalid type ${type}, are accepted only ${Object.keys(matcher).join(", ")}!`);

    setSearchTruck("");
    setSearchDriver("");
    reset((values) => ({
      ...values,
      truck: {
        id: "",
        licensePlate: ""
      },
      driver: {
        id: "",
        name: ""
      },
      capacity: 0,
      qtyOrdered: 0,
      qtyTransported: 0
    }));

    matcher[type]();
  };

  useEffect(() => {
    if (truckData) {
      const { netCapacity, driver, licensePlate } = truckData;

      if (!defaultValues?.qtyOrdered) {
        setValue("qtyOrdered", netCapacity);
      }
      setValue("capacity", netCapacity);
      setValue("driver.id", String(driver.id));
      setValue("driver.name", driver.name);
      setValue("truck.licensePlate", licensePlate);
      setSearchDriver(truckData.driver.name);
    }
  }, [truckData]);

  useEffect(() => {
    if (defaultValues) {
      const { expirationDate, qtyOrdered } = defaultValues;
      setValue("expirationDate", expirationDate!);
      setValue("qtyOrdered", qtyOrdered!);
    }
  }, [defaultValues]);

  useEffect(() => {
    if (truckId) {
      getTruckTrigger({ id: Number(truckId) });
    }
  }, [truckId]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack>
        <Input.Switch control={control} name="isFreelanceDriver" label="Autônomo" color="teal" size="md" />

        <div className="grid grid-cols-1 gap-[inherit] md:grid-cols-2">
          <Input.Select
            control={control}
            name="truck.id"
            label="Placa do Caminhão"
            placeholder="Selecione a placa"
            data={searchTrucksResult?.options}
            limit={50}
            onSearchChange={handleSearch("truck")}
            searchValue={searchTruck}
            onClear={handleClear("truck")}
            leftSection={isFetching && <Loader size="xs" />}
          />

          <Input.Select
            control={control}
            name="driver.id"
            label="Motorista"
            data={searchDriverResults?.options}
            placeholder="Selecione o motorista"
            onSearchChange={handleSearch("driver")}
            searchValue={searchDriver}
            onClear={handleClear("driver")}
            leftSection={(isFetchingDrivers || isFetchingTruck) && <Loader size="xs" />}
            readOnly={!isFreelanceDriver}
            className={disabledStyles}
          />

          <Input.Number control={control} name="qtyOrdered" min={0} label="Capacidade (t)" readOnly={!isFreelanceDriver} className={disabledStyles} />
          <Input.Date control={control} minDate={today} name="expirationDate" label="Data de Vencimento" />
        </div>

        <div className="grid grid-cols-1 gap-[inherit] md:grid-cols-2">
          <Button variant="default" onClick={handleDeny} disabled={isFetching || isFetchingDrivers} fullWidth>
            Cancelar
          </Button>

          <Tooltip label="Nenhuma mudança detectada" disabled={isDirty}>
            <Button
              type="submit"
              color={schedule?.id || schedule?.id === 0 ? "blue" : "green"}
              loading={isSubmitting || isFetching || isFetchingDrivers}
              disabled={!isDirty}
              fullWidth
            >
              {schedule?.id || schedule?.id === 0 ? "Adicionar Agendamento" : "Editar Agendamento"}
            </Button>
          </Tooltip>
        </div>

        {/* <CodeHighlightTabs
          code={[
            { fileName: "data.json", code: JSON.stringify(watch(), null, 2), language: "json" },
            { fileName: "search.json", code: JSON.stringify({ truck: searchTruck, driver: searchDriver }, null, 2), language: "json" },
            { fileName: "errors.json", code: JSON.stringify(errors, null, 2), language: "json" }
          ]}
          radius="md"
          withExpandButton
        /> */}
      </Stack>
    </form>
  );
}

export const SchedulingForm = Object.assign(SchedulingFormImpl, {
  $schema: extendedEditSchedulingArgsSchema,
  $parse: (data: z.infer<typeof extendedEditSchedulingArgsSchema>) => extendedEditSchedulingArgsSchema.parse(data),
  $safeParse: (data: z.infer<typeof extendedEditSchedulingArgsSchema>) => extendedEditSchedulingArgsSchema.safeParse(data)
});
